// @ts-nocheck
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@libraryadmin/components/layout/DashboardLayout";
import { Button } from "@libraryadmin/components/ui/button";
import { usePagination } from "@libraryadmin/hooks/usePagination";
import { DataTablePagination } from "@libraryadmin/components/ui/data-table-pagination";
import { Input } from "@libraryadmin/components/ui/input";
import { Label } from "@libraryadmin/components/ui/label";
import { Textarea } from "@libraryadmin/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@libraryadmin/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@libraryadmin/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@libraryadmin/components/ui/dialog";
import { Badge } from "@libraryadmin/components/ui/badge";
import { Skeleton } from "@libraryadmin/components/ui/skeleton";
import { Progress } from "@libraryadmin/components/ui/progress";
import {
  Warehouse,
  Plus,
  Search,
  Lock,
  Thermometer,
  Layers,
  BookOpen,
  ScrollText,
  Edit,
  Trash2,
  Eye,
  AlertCircle,
} from "lucide-react";
import { useToast } from "@libraryadmin/hooks/use-toast";
import {
  getStorageLocations,
  createStorageLocation,
  updateStorageLocation,
  deleteStorageLocation,
  getItemsInLocation,
} from "@libraryadmin/services/api";
import type { StorageLocation } from "@libraryadmin/data/seed";

const Storage = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");

  // Modals
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingLocation, setEditingLocation] =
    useState<StorageLocation | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [auditLocation, setAuditLocation] = useState<StorageLocation | null>(
    null
  );

  // Add Location Form
  const initialForm: Omit<StorageLocation, "id"> = {
    code: "",
    name: "",
    type: "rack",
    capacity: 300,
    itemCount: 0,
    locationPath: "",
    temperatureControlled: false,
    restrictedAccess: false,
    description: "",
  };
  const [form, setForm] = useState(initialForm);

  // Queries
  const {
    data: locations = [],
    isLoading,
    isError,
    error: locationsError,
  } = useQuery({
    queryKey: ["storage-locations"],
    queryFn: getStorageLocations,
  });

  const { data: auditItems, isLoading: loadingAudit } = useQuery({
    queryKey: ["location-items", auditLocation?.id],
    queryFn: () =>
      auditLocation
        ? getItemsInLocation(auditLocation.id)
        : Promise.resolve({ books: [], manuscripts: [] }),
    enabled: !!auditLocation,
  });

  const createMutation = useMutation({
    mutationFn: createStorageLocation,
    onSuccess: newLoc => {
      queryClient.invalidateQueries({ queryKey: ["storage-locations"] });
      toast({
        title: "Storage Location Added",
        description: `"${newLoc.name}" (${newLoc.code}) created.`,
      });
      setForm(initialForm);
      setShowAddDialog(false);
    },
    onError: error => {
      toast({
        title: "Unable to add storage location",
        description:
          error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<StorageLocation>;
    }) => updateStorageLocation(id, data),
    onSuccess: updated => {
      queryClient.invalidateQueries({ queryKey: ["storage-locations"] });
      toast({
        title: "Storage Location Updated",
        description: `"${updated.name}" updated.`,
      });
      setEditingLocation(null);
    },
    onError: error => {
      toast({
        title: "Unable to update storage location",
        description:
          error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteStorageLocation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["storage-locations"] });
      toast({
        title: "Storage Location Deleted",
        description: "Location record removed.",
        variant: "destructive",
      });
      setDeletingId(null);
    },
    onError: error => {
      toast({
        title: "Unable to delete storage location",
        description:
          error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    },
  });

  const filtered = locations.filter(loc => {
    const matchesSearch =
      loc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      loc.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      loc.locationPath.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || loc.type === filterType;
    return matchesSearch && matchesType;
  });

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code || !form.name) {
      return;
    }
    createMutation.mutate({
      ...form,
      locationPath: form.locationPath || form.name,
    });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingLocation) {
      return;
    }
    updateMutation.mutate({ id: editingLocation.id, data: editingLocation });
  };

  const totalCapacity = locations.reduce((sum, l) => sum + l.capacity, 0);
  const totalOccupied = locations.reduce((sum, l) => sum + l.itemCount, 0);

  const {
    currentPage,
    totalPages,
    itemsPerPage,
    paginatedData,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(filtered);

  return (
    <DashboardLayout
      title="Physical Storage & Location Management"
      subtitle="Manage vaults, racks, cupboards, and shelf inventory audit"
    >
      <div className="space-y-6">
        {/* KPI Strip */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-xl border border-border/40 bg-card p-4 shadow-sm">
            <p className="text-xs font-medium text-muted-foreground">
              Storage Units
            </p>
            <p className="mt-1 text-2xl font-bold text-primary">
              {isLoading ? "—" : locations.length}
            </p>
          </div>
          <div className="rounded-xl border border-border/40 bg-card p-4 shadow-sm">
            <p className="text-xs font-medium text-muted-foreground">
              Climate Vaults
            </p>
            <p className="mt-1 text-2xl font-bold text-info">
              {isLoading
                ? "—"
                : locations.filter(l => l.temperatureControlled).length}
            </p>
          </div>
          <div className="rounded-xl border border-border/40 bg-card p-4 shadow-sm">
            <p className="text-xs font-medium text-muted-foreground">
              Total Capacity
            </p>
            <p className="mt-1 text-2xl font-bold text-accent">
              {isLoading ? "—" : totalCapacity.toLocaleString()}
            </p>
          </div>
          <div className="rounded-xl border border-border/40 bg-card p-4 shadow-sm">
            <p className="text-xs font-medium text-muted-foreground">
              Occupancy Rate
            </p>
            <p className="mt-1 text-2xl font-bold text-success">
              {isLoading || totalCapacity === 0
                ? "—"
                : `${Math.round((totalOccupied / totalCapacity) * 100)}%`}
            </p>
          </div>
        </div>

        {/* Storage Location Grid / Table Section */}
        <div className="form-section">
          {/* Header & Add Button */}
          <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-lg font-bold text-foreground">
                Storage Units Directory
              </h2>
              <p className="text-xs text-muted-foreground">
                Showing {filtered.length} storage units
              </p>
            </div>

            <Button
              onClick={() => setShowAddDialog(true)}
              className="btn-primary w-full sm:w-auto"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Storage Location
            </Button>
          </div>

          {/* Filters Bar */}
          <div className="mb-6 grid grid-cols-1 gap-3 rounded-xl border border-border/50 bg-muted/30 p-3 sm:grid-cols-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search vault, rack code, location path..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="input-field pl-9 text-xs"
              />
            </div>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="input-field h-9 text-xs">
                <SelectValue placeholder="Storage Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Storage Types</SelectItem>
                <SelectItem value="vault">Climate Vaults</SelectItem>
                <SelectItem value="rack">Racks</SelectItem>
                <SelectItem value="cupboard">Cupboards</SelectItem>
                <SelectItem value="shelf">Shelves</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Locations Table */}
          <div className="overflow-hidden rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Code & Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Path / Location</TableHead>
                  <TableHead>Security & Climate</TableHead>
                  <TableHead>Occupancy / Capacity</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton className="h-4 w-full" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-12 text-center text-destructive"
                    >
                      {locationsError instanceof Error
                        ? locationsError.message
                        : "Unable to load storage locations."}
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-12 text-center text-muted-foreground"
                    >
                      No storage locations match criteria
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map(loc => {
                    const percent = Math.min(
                      100,
                      loc.occupancyPercentage ??
                        (loc.capacity > 0
                          ? Math.round((loc.itemCount / loc.capacity) * 100)
                          : 0)
                    );
                    return (
                      <TableRow key={loc.id} className="table-row-hover">
                        <TableCell>
                          <div>
                            <span className="font-mono text-xs font-bold text-primary">
                              {loc.code}
                            </span>
                            <p className="text-sm font-semibold text-foreground">
                              {loc.name}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className="text-xs capitalize"
                          >
                            {loc.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {loc.locationPath}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1.5">
                            {loc.temperatureControlled && (
                              <Badge
                                variant="outline"
                                className="badge-info gap-1 text-[10px]"
                              >
                                <Thermometer className="h-3 w-3" /> Climate
                                Controlled
                              </Badge>
                            )}
                            {loc.restrictedAccess && (
                              <Badge
                                variant="outline"
                                className="badge-warning gap-1 text-[10px]"
                              >
                                <Lock className="h-3 w-3" /> Restricted
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="w-40 space-y-1">
                            <div className="flex justify-between text-[11px] font-medium">
                              <span>
                                {loc.itemCount} / {loc.capacity} items
                              </span>
                              <span>{percent}%</span>
                            </div>
                            <Progress value={percent} className="h-1.5" />
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setAuditLocation(loc)}
                              title="Audit Items in this Storage"
                              className="gap-1 text-primary hover:bg-primary/10 hover:text-primary"
                            >
                              <Eye className="h-4 w-4" /> Audit Items
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingLocation(loc)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:bg-destructive/10"
                              onClick={() => setDeletingId(loc.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <DataTablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
      </div>

      {/* Add Location Modal */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary">
              <Warehouse className="h-5 w-5 text-accent" /> Add New Physical
              Storage Unit
            </DialogTitle>
            <DialogDescription>
              Define a new vault, rack, cupboard, or shelf location.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={handleCreateSubmit}
            className="space-y-4 py-2 text-xs"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Location Code *</Label>
                <Input
                  placeholder="e.g. V-D or R-7"
                  className="input-field font-mono"
                  value={form.code}
                  onChange={e => setForm({ ...form, code: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>Storage Type</Label>
                <Select
                  value={form.type}
                  onValueChange={v =>
                    setForm({ ...form, type: v as StorageLocation["type"] })
                  }
                >
                  <SelectTrigger className="input-field">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vault">Climate Vault</SelectItem>
                    <SelectItem value="rack">Rack</SelectItem>
                    <SelectItem value="cupboard">Cupboard</SelectItem>
                    <SelectItem value="shelf">Shelf</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Current Occupancy</Label>
                <Input
                  type="number"
                  min="0"
                  max={form.capacity}
                  className="input-field"
                  value={form.itemCount}
                  onChange={e =>
                    setForm({ ...form, itemCount: Number(e.target.value) })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Occupancy Percentage</Label>
                <Input
                  readOnly
                  className="input-field bg-muted/30"
                  value={`${form.capacity > 0 ? ((form.itemCount / form.capacity) * 100).toFixed(1) : "0.0"}%`}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Unit Name *</Label>
              <Input
                placeholder="e.g. Vault D (Special Collection)"
                className="input-field"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Location Path</Label>
                <Input
                  placeholder="e.g. Rack 7 / Shelves A-C"
                  className="input-field"
                  value={form.locationPath}
                  onChange={e =>
                    setForm({ ...form, locationPath: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Capacity (Items)</Label>
                <Input
                  type="number"
                  placeholder="300"
                  className="input-field"
                  value={form.capacity}
                  onChange={e =>
                    setForm({ ...form, capacity: Number(e.target.value) })
                  }
                />
              </div>
            </div>

            <div className="flex gap-6 pt-2">
              <label className="flex cursor-pointer items-center gap-2 font-medium">
                <input
                  type="checkbox"
                  checked={form.temperatureControlled}
                  onChange={e =>
                    setForm({
                      ...form,
                      temperatureControlled: e.target.checked,
                    })
                  }
                  className="h-4 w-4 rounded border-border text-primary"
                />
                Climate & Temperature Controlled
              </label>
              <label className="flex cursor-pointer items-center gap-2 font-medium text-warning">
                <input
                  type="checkbox"
                  checked={form.restrictedAccess}
                  onChange={e =>
                    setForm({ ...form, restrictedAccess: e.target.checked })
                  }
                  className="h-4 w-4 rounded border-border text-warning"
                />
                Restricted Vault Access
              </label>
            </div>

            <div className="space-y-1">
              <Label>Description & Notes</Label>
              <Textarea
                placeholder="Storage notes..."
                className="input-field min-h-[70px]"
                value={form.description}
                onChange={e =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>

            <DialogFooter className="border-t pt-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddDialog(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
                className="btn-primary"
              >
                {createMutation.isPending ? "Saving..." : "Save Location"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Audit Modal (Shows items inside the selected storage unit) */}
      <Dialog
        open={!!auditLocation}
        onOpenChange={open => !open && setAuditLocation(null)}
      >
        <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-primary">
              <Layers className="h-5 w-5 text-accent" /> Storage Audit:{" "}
              {auditLocation?.name}
            </DialogTitle>
            <DialogDescription className="font-mono text-xs">
              Path: {auditLocation?.locationPath}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 text-xs">
            {loadingAudit ? (
              <p className="py-8 text-center text-muted-foreground">
                Scanning storage unit items...
              </p>
            ) : (
              <>
                <div className="flex gap-4 rounded-lg border bg-muted/30 p-3">
                  <div>
                    <span className="block text-muted-foreground">
                      Matched Books
                    </span>
                    <span className="text-sm font-bold text-foreground">
                      {auditItems?.books.length}
                    </span>
                  </div>
                  <div>
                    <span className="block text-muted-foreground">
                      Matched Manuscripts
                    </span>
                    <span className="text-sm font-bold text-primary">
                      {auditItems?.manuscripts.length}
                    </span>
                  </div>
                </div>

                {auditItems?.manuscripts &&
                  auditItems.manuscripts.length > 0 && (
                    <div>
                      <h4 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-primary">
                        <ScrollText className="h-4 w-4" /> Manuscripts Stored
                        Here
                      </h4>
                      <div className="overflow-hidden rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/40">
                              <TableHead>MS No</TableHead>
                              <TableHead>Title</TableHead>
                              <TableHead>Language</TableHead>
                              <TableHead>Condition</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {auditItems.manuscripts.map(m => (
                              <TableRow key={m.id}>
                                <TableCell className="font-mono">
                                  {m.manuscriptNo}
                                </TableCell>
                                <TableCell className="font-semibold">
                                  {m.title}
                                </TableCell>
                                <TableCell>{m.language}</TableCell>
                                <TableCell>
                                  <Badge variant="outline">{m.condition}</Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <DataTablePagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          itemsPerPage={itemsPerPage}
                          onPageChange={handlePageChange}
                          onItemsPerPageChange={handleItemsPerPageChange}
                        />
                      </div>
                    </div>
                  )}

                {auditItems?.books && auditItems.books.length > 0 && (
                  <div>
                    <h4 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-foreground">
                      <BookOpen className="h-4 w-4" /> Books Stored Here
                    </h4>
                    <div className="overflow-hidden rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/40">
                            <TableHead>Book No</TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Author</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {auditItems.books.map(b => (
                            <TableRow key={b.id}>
                              <TableCell className="font-mono">
                                {b.bookNo}
                              </TableCell>
                              <TableCell className="font-semibold">
                                {b.title}
                              </TableCell>
                              <TableCell>{b.author}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{b.status}</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <DataTablePagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        onItemsPerPageChange={handleItemsPerPageChange}
                      />
                    </div>
                  </div>
                )}

                {!auditItems?.books?.length &&
                  !auditItems?.manuscripts?.length && (
                    <p className="py-6 text-center text-muted-foreground">
                      No specific items mapped to this exact path prefix yet.
                    </p>
                  )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button onClick={() => setAuditLocation(null)}>Close Audit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Location Modal */}
      <Dialog
        open={!!editingLocation}
        onOpenChange={open => !open && setEditingLocation(null)}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Storage Unit</DialogTitle>
          </DialogHeader>
          {editingLocation && (
            <form
              onSubmit={handleEditSubmit}
              className="space-y-4 py-2 text-xs"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Code</Label>
                  <Input
                    value={editingLocation.code}
                    onChange={e =>
                      setEditingLocation({
                        ...editingLocation,
                        code: e.target.value,
                      })
                    }
                    className="input-field font-mono"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Name</Label>
                  <Input
                    value={editingLocation.name}
                    onChange={e =>
                      setEditingLocation({
                        ...editingLocation,
                        name: e.target.value,
                      })
                    }
                    className="input-field"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Current Occupancy</Label>
                  <Input
                    type="number"
                    min="0"
                    max={editingLocation.capacity}
                    value={editingLocation.itemCount}
                    onChange={e =>
                      setEditingLocation({
                        ...editingLocation,
                        itemCount: Number(e.target.value),
                      })
                    }
                    className="input-field"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Occupancy Percentage</Label>
                  <Input
                    readOnly
                    value={`${editingLocation.capacity > 0 ? ((editingLocation.itemCount / editingLocation.capacity) * 100).toFixed(1) : "0.0"}%`}
                    className="input-field bg-muted/30"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Path</Label>
                  <Input
                    value={editingLocation.locationPath}
                    onChange={e =>
                      setEditingLocation({
                        ...editingLocation,
                        locationPath: e.target.value,
                      })
                    }
                    className="input-field"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Capacity</Label>
                  <Input
                    type="number"
                    value={editingLocation.capacity}
                    onChange={e =>
                      setEditingLocation({
                        ...editingLocation,
                        capacity: Number(e.target.value),
                      })
                    }
                    className="input-field"
                  />
                </div>
              </div>

              <div className="flex gap-6 pt-2">
                <label className="flex cursor-pointer items-center gap-2 font-medium">
                  <input
                    type="checkbox"
                    checked={editingLocation.temperatureControlled}
                    onChange={e =>
                      setEditingLocation({
                        ...editingLocation,
                        temperatureControlled: e.target.checked,
                      })
                    }
                    className="h-4 w-4 rounded border-border text-primary"
                  />
                  Climate Controlled
                </label>
                <label className="flex cursor-pointer items-center gap-2 font-medium text-warning">
                  <input
                    type="checkbox"
                    checked={editingLocation.restrictedAccess}
                    onChange={e =>
                      setEditingLocation({
                        ...editingLocation,
                        restrictedAccess: e.target.checked,
                      })
                    }
                    className="h-4 w-4 rounded border-border text-warning"
                  />
                  Restricted Access
                </label>
              </div>

              <DialogFooter className="mt-4 border-t pt-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setEditingLocation(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="btn-primary"
                >
                  {updateMutation.isPending ? "Saving..." : "Save Changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={!!deletingId}
        onOpenChange={open => !open && setDeletingId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" /> Delete Storage Location
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this storage location record?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingId && deleteMutation.mutate(deletingId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Confirm Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Storage;
